import { useEffect, useState, useCallback, useMemo } from "react";
import { Icon, Avatar, EmptyState } from "../../components/Common.jsx";
import { useApp } from "../../store.jsx";
import { fmtDate, fmtDateTime, fmtRelative, computeTrialEndFromCreation, daysBetween } from "../../lib/dates.js";
import { formatMoney, rupeesToPaise, paiseToRupees } from "../../lib/money.js";
import TableSkeleton from "../../components/admin/TableSkeleton.jsx";
import AdminPageBoundary from "../../components/admin/AdminPageBoundary.jsx";
import InvoiceDetailModal from "../../components/admin/InvoiceDetailModal.jsx";
import {
  getCompany,
  listPlans,
  extendTrial,
  assignPlanToCompany,
  suspendCompany,
  unsuspendCompany,
  deleteCompany,
  hardDeleteCompany,
  updateCompany,
  changePlan,
  cancelSubscription,
  reactivateSub,
  markInvoicePaid,
  voidInvoice,
  refundInvoice,
} from "../../lib/adminApi";

const TABS = ["Overview", "Subscription", "Invoices", "Notes", "Danger Zone"];

const bentoCard = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: 16,
  padding: 18,
};

const SUB_STATUS_LABEL = {
  active: "Active",
  past_due: "Past Due",
  trialing: "Trialing",
  canceled: "Canceled",
  pending: "Pending",
};

const INVOICE_STATUS_LABEL = {
  paid: "Paid",
  unpaid: "Unpaid",
  voided: "Voided",
  partially_refunded: "Partially refunded",
  refunded: "Refunded",
};

function Info({ label, value, icon }) {
  return (
    <div style={bentoCard}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1.2, color: "var(--text-muted)", fontWeight: 600 }}>
            {label}
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, marginTop: 6 }}>{value}</div>
        </div>
        <div style={{ width: 32, height: 32, borderRadius: 9, display: "grid", placeItems: "center", background: "rgba(139,92,246,.12)", color: "#8B5CF6" }}>
          <Icon name={icon} size={16} />
        </div>
      </div>
    </div>
  );
}

function DangerRow({ title, desc, icon, buttonLabel, danger, onClick }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", borderBottom: "1px solid var(--border)" }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{desc}</div>
      </div>
      <button
        className="btn btn-sm"
        style={danger ? { background: "var(--error)", color: "#fff", border: "1px solid var(--error)" } : undefined}
        onClick={onClick}
      >
        <Icon name={icon} size={14} /> {buttonLabel}
      </button>
    </div>
  );
}

function CompanyDetailInner() {
  const { pageContext, goPage, toast, openModal, closeModal } = useApp();
  const id = pageContext?.companyId;
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [tab, setTab] = useState("Overview");
  const [notes, setNotes] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const c = await getCompany(id);
      setData(c);
      setNotes(c.notes || "");
      setNotesDirty(false);
    } catch (e) {
      toast(e.message || "Failed to load company", "error");
    }
  }, [id, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { listPlans().then(setPlans).catch(() => setPlans([])); }, []);

  const currentPlan = useMemo(
    () => plans.find((p) => p.key === data?.subscriptionPlan) || null,
    [plans, data?.subscriptionPlan],
  );
  const planTrialDays = Number(currentPlan?.features?.trialDays) || 0;
  const computedTrialEnd = useMemo(() => {
    if (data?.trialEnd) return new Date(data.trialEnd);
    if (planTrialDays > 0 && data?.createdAt) return computeTrialEndFromCreation(data.createdAt, planTrialDays);
    return null;
  }, [data?.trialEnd, data?.createdAt, planTrialDays]);

  const baseTrialEnd = useMemo(
    () => (data?.createdAt && planTrialDays > 0 ? computeTrialEndFromCreation(data.createdAt, planTrialDays) : null),
    [data?.createdAt, planTrialDays],
  );
  const extendedDays = useMemo(() => {
    if (!baseTrialEnd || !data?.trialEnd) return 0;
    return Math.max(0, daysBetween(baseTrialEnd, new Date(data.trialEnd)) || 0);
  }, [baseTrialEnd, data?.trialEnd]);
  const trialDaysRemaining = useMemo(() => {
    if (!data?.trialEnd) return null;
    return Math.max(0, daysBetween(new Date(), new Date(data.trialEnd)) || 0);
  }, [data?.trialEnd]);

  if (!id) {
    return (
      <section>
        <EmptyState
          title="No company selected"
          desc="Pick a company from the Companies list."
          icon="building"
          cta="Back to Companies"
          onCta={() => goPage("adminCompanies")}
        />
      </section>
    );
  }
  if (!data) {
    return (
      <section>
        <TableSkeleton columns={4} rows={3} />
      </section>
    );
  }

  const subscription = data.subscription;
  const subAmount = subscription?.planAmount || currentPlan?.amount || 0;
  // Renewal date passed but status not yet swept to past_due/canceled.
  const subLapsed =
    !!subscription &&
    subscription.status !== "canceled" &&
    !!subscription.currentPeriodEnd &&
    new Date(subscription.currentPeriodEnd) < new Date();

  const onSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await updateCompany(id, { notes });
      toast("Notes saved", "success");
      setNotesDirty(false);
    } catch (e) { toast(e.message, "error"); }
    finally { setSavingNotes(false); }
  };

  const onTabChange = (next) => {
    if (notesDirty && tab === "Notes") {
      if (!window.confirm("You have unsaved notes. Discard?")) return;
      setNotes(data.notes || "");
      setNotesDirty(false);
    }
    setTab(next);
  };

  const onExtendTrial = () => {
    let days = 14;
    let reason = "";
    openModal(
      <>
        <div className="modal-head">
          <h3>Extend trial</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="field">
              <label>Days (1-60)</label>
              <input
                className="input"
                type="number"
                min={1}
                max={60}
                defaultValue={days}
                onChange={(e) => { days = Number(e.target.value); }}
              />
            </div>
          </div>
          <div className="field">
            <label>Reason (required, ≥4 chars)</label>
            <textarea className="input" rows={3} onChange={(e) => { reason = e.target.value; }} />
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn btn-primary" onClick={async () => {
            if (!days || days < 1) return toast("Days must be 1-60", "error");
            if (!reason || reason.trim().length < 4) return toast("Reason required (≥4 chars)", "error");
            try {
              await extendTrial(id, days, reason);
              toast("Trial extended", "success");
              closeModal();
              load();
            } catch (e) { toast(e.message, "error"); }
          }}>
            <Icon name="calendar-plus" size={14} /> Extend
          </button>
        </div>
      </>,
    );
  };

  const onChangePlan = () => {
    let nextPlan = "starter";
    const isAssignNew = !subscription;
    openModal(
      <>
        <div className="modal-head">
          <h3>{isAssignNew ? "Assign paid plan" : "Change plan"}</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          {isAssignNew ? (
            <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
              Company is on free trial. Assigning a paid plan creates a monthly Subscription, marks the company Active,
              and bills the customer for 1 month starting today.
            </p>
          ) : (
            <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
              Plan change replaces the current billing cycle. New period
              end = today + 30 days.
            </p>
          )}
          <div className="field">
            <label>{isAssignNew ? "Plan" : "New plan"}</label>
            <select className="input" defaultValue={nextPlan} onChange={(e) => { nextPlan = e.target.value; }}>
              {plans
                .filter((p) => p.key !== "free_trial" && p.isActive)
                .map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.name} — {formatMoney(p.amount, p.currency || "INR")} / month
                  </option>
                ))}
            </select>
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn btn-primary" onClick={async () => {
            try {
              if (isAssignNew) {
                await assignPlanToCompany(id, nextPlan, 1);
                toast("Plan assigned", "success");
              } else {
                await changePlan(subscription.id, nextPlan, 1);
                toast("Plan changed", "success");
              }
              closeModal();
              load();
            } catch (e) { toast(e.message, "error"); }
          }}>
            <Icon name="repeat" size={14} /> {isAssignNew ? "Assign" : "Apply"}
          </button>
        </div>
      </>,
    );
  };


  const onCancelSub = () => {
    openModal(
      <>
        <div className="modal-head">
          <h3>Cancel subscription</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Cancel <strong>{data.name}</strong>'s subscription. Pick mode:
          </p>
        </div>
        <div className="modal-foot" style={{ gap: 8, flexWrap: "wrap" }}>
          <button className="btn btn-ghost" onClick={closeModal}>Keep</button>
          <button className="btn" onClick={async () => {
            try { await cancelSubscription(subscription.id, "period_end"); toast("Will cancel at period end", "success"); closeModal(); load(); }
            catch (e) { toast(e.message, "error"); }
          }}>
            <Icon name="calendar-x" size={14} /> At Period End
          </button>
          <button className="btn" style={{ background: "var(--error)", color: "#fff" }} onClick={async () => {
            try { await cancelSubscription(subscription.id, "now"); toast("Canceled", "success"); closeModal(); load(); }
            catch (e) { toast(e.message, "error"); }
          }}>
            <Icon name="x-circle" size={14} /> Cancel Now
          </button>
        </div>
      </>,
    );
  };

  const onSuspendModal = () => {
    let reason = "";
    openModal(
      <>
        <div className="modal-head">
          <h3>{data.suspendedAt ? "Unsuspend" : "Suspend"} company</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        {!data.suspendedAt && (
          <div className="modal-body">
            <div className="field">
              <label>Reason (≥4 chars)</label>
              <textarea className="input" rows={3} onChange={(e) => { reason = e.target.value; }} />
            </div>
          </div>
        )}
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button
            className="btn"
            style={!data.suspendedAt ? { background: "var(--error)", color: "#fff" } : undefined}
            onClick={async () => {
              try {
                if (data.suspendedAt) await unsuspendCompany(id);
                else {
                  if (!reason || reason.trim().length < 4) return toast("Reason required", "error");
                  await suspendCompany(id, reason);
                }
                toast("Saved", "success");
                closeModal();
                load();
              } catch (e) { toast(e.message, "error"); }
            }}
          >
            <Icon name={data.suspendedAt ? "play" : "ban"} size={14} />{" "}
            {data.suspendedAt ? "Unsuspend" : "Suspend"}
          </button>
        </div>
      </>,
    );
  };

  const onDelete = () => {
    let typed = "";
    openModal(
      <>
        <div className="modal-head">
          <h3 style={{ color: "var(--error)" }}>
            <Icon name="alert-triangle" size={16} /> Delete {data.name}
          </h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Soft delete — type the slug <code>{data.slug}</code> to confirm.
          </p>
          <div className="field">
            <input className="input" placeholder={`Type "${data.slug}" to confirm`} onChange={(e) => { typed = e.target.value; }} />
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn" style={{ background: "var(--error)", color: "#fff" }} onClick={async () => {
            if (typed !== data.slug) return toast("Slug mismatch", "error");
            try { await deleteCompany(id); toast("Deleted", "success"); closeModal(); goPage("adminCompanies"); }
            catch (e) { toast(e.message, "error"); }
          }}>
            <Icon name="trash-2" size={14} /> Delete
          </button>
        </div>
      </>,
    );
  };

  const onHardDelete = () => {
    let pass = "";
    openModal(
      <>
        <div className="modal-head">
          <h3 style={{ color: "var(--error)" }}>
            <Icon name="skull" size={16} /> Hard delete {data.name}
          </h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ background: "rgba(239,68,68,0.1)", borderRadius: 8, padding: 12, marginBottom: 14 }}>
            <p style={{ fontSize: 12, color: "var(--error)", fontWeight: 600, margin: 0 }}>
              <Icon name="alert-triangle" size={14} /> This permanently removes all data — users, reports, tasks, invoices, subscription, and the company itself. This cannot be undone.
            </p>
          </div>
          <div className="field">
            <label>Enter your password to confirm</label>
            <input
              className="input"
              type="password"
              placeholder="Your admin password"
              onChange={(e) => { pass = e.target.value; }}
            />
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn" style={{ background: "var(--error)", color: "#fff" }} onClick={async () => {
            if (!pass || pass.length < 1) return toast("Password required", "error");
            try { await hardDeleteCompany(id, pass); toast("Company permanently deleted", "success"); closeModal(); goPage("adminCompanies"); }
            catch (e) { toast(e.message, "error"); }
          }}>
            <Icon name="trash-2" size={14} /> Delete Permanently
          </button>
        </div>
      </>,
    );
  };

  const onRefundInvoice = (inv) => {
    const refunded = inv.refundedAmount || 0;
    const available = Math.max(0, inv.amount - refunded);
    let amountRupees = paiseToRupees(available);
    let reason = "";
    openModal(
      <>
        <div className="modal-head">
          <h3>Refund invoice</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Available to refund: <strong>{formatMoney(available, inv.currency || "INR")}</strong>
          </p>
          <div className="field">
            <label>Refund amount (₹)</label>
            <input
              className="input"
              type="number"
              min={0}
              step="0.01"
              defaultValue={amountRupees}
              onChange={(e) => { amountRupees = Number(e.target.value); }}
            />
          </div>
          <div className="field">
            <label>Reason (≥4 chars)</label>
            <textarea className="input" rows={3} onChange={(e) => { reason = e.target.value; }} />
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn btn-primary" onClick={async () => {
            if (!reason || reason.trim().length < 4) return toast("Reason required", "error");
            const amountPaise = rupeesToPaise(amountRupees);
            if (amountPaise <= 0 || amountPaise > available) return toast("Invalid amount", "error");
            try {
              const idem = crypto.randomUUID();
              await refundInvoice(inv.id, amountPaise, reason, idem);
              toast("Refund issued", "success");
              closeModal();
              load();
            } catch (e) { toast(e.message, "error"); }
          }}>
            <Icon name="corner-down-left" size={14} /> Refund
          </button>
        </div>
      </>,
    );
  };

  const onViewInvoice = (inv) => {
    openModal(
      <InvoiceDetailModal invoiceId={inv.id} onClose={closeModal} />,
      "lg",
    );
  };

  const onConfirm = (action) => () => setPendingAction(action);
  const runConfirmed = async () => {
    if (!pendingAction) return;
    try {
      await pendingAction.run();
      toast(pendingAction.successMsg || "Done", "success");
      load();
    } catch (e) {
      toast(e.message || "Failed", "error");
    } finally {
      setPendingAction(null);
    }
  };

  const deletedRow = data.suspendReason === "soft_deleted";
  const displayName =
    typeof data.name === "string" ? data.name.replace(/^(\[DELETED\]\s+)+/i, "[DELETED] ") : data.name;

  const statusBadge = deletedRow
    ? <span className="badge badge-error"><span className="badge-dot" /> Deleted</span>
    : data.suspendedAt
      ? <span className="badge badge-error"><span className="badge-dot" /> Suspended</span>
      : data.isActive
        ? <span className="badge badge-success"><span className="badge-dot" /> Active</span>
        : <span className="badge badge-muted"><span className="badge-dot" /> Inactive</span>;

  return (
    <section>
      <div className="page-head">
        <div>
          <button className="btn btn-ghost btn-sm" onClick={() => goPage("adminCompanies")}>
            <Icon name="arrow-left" size={14} /> Companies
          </button>
          <h1 className="page-title" style={{ marginTop: 10 }}>
            <Avatar name={displayName} size="sm" seed={displayName?.length || 0} />
            <span style={{ marginLeft: 10 }}>{displayName}</span>
          </h1>
          <p className="page-sub">
            {data.slug} · {statusBadge}
          </p>
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: 16 }}>
        {TABS.map((t) => (
          <div
            key={t}
            className={`tab ${tab === t ? "active" : ""}`}
            onClick={() => onTabChange(t)}
          >
            {t}
          </div>
        ))}
      </div>

      {tab === "Overview" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
          <Info label="Plan" value={currentPlan?.name || data.subscriptionPlan || "—"} icon="tag" />
          <Info label="Users" value={data._count?.users ?? 0} icon="users" />
          <Info label="Reports" value={data._count?.reports ?? 0} icon="file-text" />
          <Info label="Start Date" value={fmtDate(data.createdAt)} icon="calendar" />
          <Info
            label={`Trial Ends ${planTrialDays ? `(plan: ${planTrialDays}d)` : ""}`}
            value={computedTrialEnd ? fmtDate(computedTrialEnd) : "—"}
            icon="clock"
          />
          <Info
            label="Subscription"
            value={subscription ? `${currentPlan?.name || subscription.plan} · ${formatMoney(subAmount)}` : "Trial"}
            icon="repeat"
          />
        </div>
      )}

      {tab === "Subscription" && (
        <div className="card">
          {subscription ? (
            <div>
              {(subLapsed || data.isActive === false) && (
                <div
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    background: "rgba(239,68,68,.12)", border: "1px solid rgba(239,68,68,.4)",
                    color: "var(--error)", borderRadius: 8, padding: "10px 12px",
                    fontSize: 13, marginBottom: 16,
                  }}
                >
                  <Icon name="alert-triangle" size={16} />
                  <span>
                    {data.isActive === false
                      ? "This company is blocked — its subscription expired past the 3-day grace period."
                      : "Renewal date has passed. The daily sweep will mark it Past Due, then block it after a 3-day grace period."}
                  </span>
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 18 }}>
                <Info label="Plan" value={currentPlan?.name || subscription.plan} icon="tag" />
                <Info label="Amount" value={formatMoney(subAmount)} icon="indian-rupee" />
                <Info
                  label="Status"
                  value={
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      {SUB_STATUS_LABEL[subscription.status] || subscription.status}
                      {subLapsed && subscription.status === "active" && (
                        <span className="badge badge-error"><span className="badge-dot" /> Expired</span>
                      )}
                    </span>
                  }
                  icon="activity"
                />
                <Info label="Created" value={fmtDateTime(subscription.createdAt)} icon="clock" />
                <Info
                  label="Period End"
                  value={
                    <span style={{ color: subLapsed ? "var(--error)" : undefined, fontWeight: subLapsed ? 700 : undefined }}>
                      {fmtDate(subscription.currentPeriodEnd)}{subLapsed ? " · expired" : ""}
                    </span>
                  }
                  icon="calendar"
                />
                {subscription.cancelledAt && (
                  <Info label="Cancelled At" value={fmtDateTime(subscription.cancelledAt)} icon="x-circle" />
                )}
                {subscription.lastChangedByEmail && (
                  <Info
                    label={`Last ${subscription.lastChangedSource === "admin" ? "changed (admin)" : "purchased (customer)"}`}
                    value={
                      <>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>{subscription.lastChangedByName || subscription.lastChangedByEmail}</div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                          {subscription.lastChangedByEmail}{subscription.lastChangedAt ? ` · ${fmtRelative(subscription.lastChangedAt)}` : ""}
                        </div>
                      </>
                    }
                    icon="user"
                  />
                )}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <button className="btn btn-ghost btn-sm" onClick={onChangePlan}>
                  <Icon name="repeat" size={14} /> Change Plan
                </button>
                {subscription.status === "canceled" ? (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={onConfirm({
                      title: "Reactivate subscription?",
                      desc: "The customer regains access immediately and Razorpay collection resumes.",
                      successMsg: "Reactivated",
                      run: () => reactivateSub(subscription.id),
                    })}
                  >
                    <Icon name="play" size={14} /> Reactivate
                  </button>
                ) : (
                  <button className="btn btn-sm" style={{ color: "var(--error)" }} onClick={onCancelSub}>
                    <Icon name="x-circle" size={14} /> Cancel
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 18 }}>
                <Info label="Plan" value={currentPlan?.name || "Free Trial"} icon="tag" />
                <Info label="Base Trial" value={`${planTrialDays || 0} days`} icon="clock" />
                <Info label="Trial Ends" value={computedTrialEnd ? fmtDate(computedTrialEnd) : "—"} icon="calendar" />
                <Info label="Created" value={fmtDate(data.createdAt)} icon="calendar" />
                {extendedDays > 0 && (
                  <Info
                    label="Extended By"
                    value={`+${extendedDays} day${extendedDays === 1 ? "" : "s"}`}
                    icon="calendar-plus"
                  />
                )}
                {trialDaysRemaining != null && (
                  <Info
                    label="Days Remaining"
                    value={trialDaysRemaining > 0 ? `${trialDaysRemaining} day${trialDaysRemaining === 1 ? "" : "s"}` : "Expired"}
                    icon="timer"
                  />
                )}
              </div>
              <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 14 }}>
                No paid subscription yet. As platform admin you can extend the
                trial, assign a paid plan now, or grant free time after the
                first plan is assigned.
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <button className="btn btn-ghost btn-sm" onClick={onExtendTrial}>
                  <Icon name="calendar-plus" size={14} /> Extend Trial
                </button>
                <button className="btn btn-primary btn-sm" onClick={onChangePlan}>
                  <Icon name="repeat" size={14} /> Assign Paid Plan
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "Invoices" && (
        ((data.invoices || []).length === 0)
          ? <EmptyState title="No invoices" desc="No invoices yet." icon="receipt" />
          : (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr><th>ID</th><th>Period</th><th>Amount</th><th>Refunded</th><th>Status</th><th>Created</th><th style={{ width: 160 }}>Actions</th></tr>
                </thead>
                <tbody>
                  {data.invoices.map((inv) => {
                    const refunded = inv.refundedAmount || 0;
                    return (
                      <tr key={inv.id}>
                        <td data-label="ID" style={{ fontFamily: "monospace", fontSize: 12 }}>{inv.id.slice(0, 8)}</td>
                        <td data-label="Period" style={{ fontSize: 12 }}>
                          {fmtDate(inv.periodStart)} – {fmtDate(inv.periodEnd)}
                        </td>
                        <td data-label="Amount"><strong>{formatMoney(inv.amount, inv.currency || "INR")}</strong></td>
                        <td data-label="Refunded">
                          {refunded > 0 ? (
                            <strong style={{ color: "var(--info)" }}>{formatMoney(refunded, inv.currency || "INR")}</strong>
                          ) : (
                            <span style={{ color: "var(--text-muted)" }}>—</span>
                          )}
                        </td>
                        <td data-label="Status">
                          <span className={`badge ${inv.status === "paid" ? "badge-success" : inv.status === "unpaid" ? "badge-warning" : inv.status === "refunded" ? "badge-info" : inv.status === "partially_refunded" ? "badge-info" : "badge-muted"}`}>
                            <span className="badge-dot" /> {INVOICE_STATUS_LABEL[inv.status] || inv.status}
                          </span>
                        </td>
                        <td data-label="Created" style={{ fontSize: 12, color: "var(--text-muted)" }}>
                          {fmtRelative(inv.createdAt)}
                        </td>
                        <td data-label="">
                          <div className="row-actions">
                            <button className="icon-btn" title="View" onClick={() => onViewInvoice(inv)}>
                              <Icon name="eye" size={14} />
                            </button>
                            {inv.status === "unpaid" && (
                              <button
                                className="icon-btn"
                                title="Mark Paid"
                                onClick={onConfirm({
                                  title: `Mark invoice ${inv.id.slice(0, 8)} as paid?`,
                                  desc: "This won't trigger a real Razorpay charge. Use only when payment was received outside the gateway.",
                                  successMsg: "Marked paid",
                                  run: () => markInvoicePaid(inv.id),
                                })}
                              ><Icon name="check-circle" size={14} /></button>
                            )}
                            {(inv.status === "paid" || inv.status === "partially_refunded") && (inv.amount - refunded) > 0 && (
                              <button className="icon-btn" title="Refund" onClick={() => onRefundInvoice(inv)}>
                                <Icon name="corner-down-left" size={14} />
                              </button>
                            )}
                            {inv.status === "unpaid" && (
                              <button
                                className="icon-btn"
                                title="Void"
                                style={{ color: "var(--error)" }}
                                onClick={onConfirm({
                                  title: "Void this unpaid invoice?",
                                  desc: "Voiding can't be undone.",
                                  successMsg: "Voided",
                                  run: () => voidInvoice(inv.id),
                                })}
                              ><Icon name="x-circle" size={14} /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
      )}

      {tab === "Notes" && (
        <div className="card">
          <h3 style={{ fontSize: 15, marginBottom: 10 }}>Internal notes</h3>
          <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
            Visible to platform admins only.
          </p>
          <textarea
            className="input"
            rows={8}
            value={notes}
            onChange={(e) => { setNotes(e.target.value); setNotesDirty(true); }}
            placeholder="Customer history, special arrangements, escalations…"
            style={{ width: "100%" }}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button className="btn btn-primary" onClick={onSaveNotes} disabled={savingNotes || !notesDirty}>
              <Icon name="check" size={14} /> {savingNotes ? "Saving…" : "Save notes"}
            </button>
            {notesDirty && (
              <button
                className="btn btn-ghost"
                onClick={() => { setNotes(data.notes || ""); setNotesDirty(false); }}
              >
                Discard
              </button>
            )}
          </div>
        </div>
      )}

      {tab === "Danger Zone" && (
        <div className="card" style={{ borderColor: "rgba(239,68,68,0.4)" }}>
          <h3 style={{ fontSize: 15, marginBottom: 6, color: "var(--error)" }}>Danger zone</h3>
          <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 16 }}>
            These actions affect access and data — proceed carefully.
          </p>

          <DangerRow
            title={data.suspendedAt ? "Unsuspend company" : "Suspend company"}
            desc={data.suspendedAt
              ? "Restore tenant access. Users will be able to log in again."
              : "Block all logins for this tenant. Reason is recorded."}
            icon={data.suspendedAt ? "play" : "ban"}
            buttonLabel={data.suspendedAt ? "Unsuspend" : "Suspend"}
            danger={!data.suspendedAt}
            onClick={onSuspendModal}
          />
          <DangerRow
            title="Delete company (soft)"
            desc="Anonymize and deactivate. Slug must be typed to confirm."
            icon="trash-2"
            buttonLabel="Delete"
            danger
            onClick={onDelete}
          />
          <DangerRow
            title="Delete company (hard)"
            desc="Permanently remove all data. Enter superadmin password to confirm."
            icon="skull"
            buttonLabel="Delete Permanently"
            danger
            onClick={onHardDelete}
          />
        </div>
      )}

      {pendingAction && (
        <div
          onClick={() => setPendingAction(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 900 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="card"
            style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)",
              width: "92%", maxWidth: 440, zIndex: 901,
              padding: 22,
            }}
          >
            <h3 style={{ fontSize: 15, marginBottom: 6 }}>{pendingAction.title}</h3>
            <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 14 }}>{pendingAction.desc}</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => setPendingAction(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={runConfirmed}>Confirm</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default function AdminCompanyDetail() {
  return (
    <AdminPageBoundary>
      <CompanyDetailInner />
    </AdminPageBoundary>
  );
}
